import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, IMAGES, waLink } from "@/config";

const LINKS = [
  { label: "Akademi", href: "/" },
  { label: "Hakkımda", href: "#hakkimda" },
  { label: "Eğitimler", href: "#egitimler" },
  { label: "Danışmanlık", href: "#danismanlik" },
  { label: "Atölyeler", href: "#etkinlikler" },
  { label: "İletişim", href: "#iletisim" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-navy/85 backdrop-blur-xl border-b border-gold/15 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="flex h-12 w-12 items-center justify-center transition-transform duration-500 group-hover:scale-105">
            <img
              src={IMAGES.logoN}
              alt="Neksus logo"
              className="h-full w-full object-contain drop-shadow-[0_0_14px_rgba(212,175,55,0.5)]"
            />
          </span>
          <span className="font-serif text-xl tracking-[0.25em] text-gold">{BRAND.name}</span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="link-underline text-sm tracking-wide text-mist hover:text-cream transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink("Merhaba, danışmanlık randevusu almak istiyorum.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-sm font-medium text-ink transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_25px_rgba(212,175,55,0.35)]"
          >
            Randevu Al
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-cream p-2"
            aria-label="Menü"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-navy/95 backdrop-blur-xl border-b border-gold/15"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-serif text-2xl text-cream"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={waLink("Merhaba, danışmanlık randevusu almak istiyorum.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-mobile-cta"
                className="mt-2 inline-flex w-fit items-center rounded-full bg-gold px-6 py-2.5 text-sm font-medium text-ink"
              >
                Randevu Al
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
